import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { USER_API } from "config";
import { budgetKeys } from "queries";
import type {
  TaxIncentiveCatalogResponse,
  TaxIncentiveJurisdiction,
} from "types/tax-incentive-catalog";
import { hydrateStructuredTaxIncentiveCatalog } from "components/dashboard/body/projects/panels/budget/TaxIncentives/taxIncentiveStructuredCatalog";
import { useAxios } from "../useAxios";

const ONE_HOUR_MS = 60 * 60 * 1000;

interface UseTaxIncentiveCatalogParams {
  enabled?: boolean;
}

export const useTaxIncentiveCatalog = ({ enabled = true }: UseTaxIncentiveCatalogParams = {}) => {
  const axios = useAxios({ allResponse: false });

  const query = useQuery({
    queryKey: budgetKeys.taxIncentiveCatalog(),
    queryFn: async (): Promise<TaxIncentiveCatalogResponse> => {
      const response = await axios.get<{ data: TaxIncentiveCatalogResponse }>(
        USER_API.getTaxIncentiveCatalog()
      );
      const payload = response as unknown;
      if (payload && typeof payload === "object" && "jurisdictions" in payload) {
        return payload as TaxIncentiveCatalogResponse;
      }
      if (payload && typeof payload === "object" && "data" in payload) {
        return (payload as { data: TaxIncentiveCatalogResponse }).data;
      }
      throw new Error("Invalid tax incentive catalog response");
    },
    enabled,
    staleTime: ONE_HOUR_MS,
    refetchOnWindowFocus: false,
  });

  // The structured catalog is a module cache filled after render, so calculations that read
  // it must wait for `isReady` (and list it as a dependency) to see the hydrated programs.
  const [hydratedJurisdictions, setHydratedJurisdictions] = useState<
    TaxIncentiveJurisdiction[] | null
  >(null);

  useEffect(() => {
    if (query.data?.jurisdictions) {
      hydrateStructuredTaxIncentiveCatalog(query.data.jurisdictions);
      setHydratedJurisdictions(query.data.jurisdictions);
    }
  }, [query.data?.jurisdictions]);

  const jurisdictions: TaxIncentiveJurisdiction[] = query.data?.jurisdictions ?? [];
  const meta = query.data?.meta;

  return {
    jurisdictions,
    meta,
    isReady: hydratedJurisdictions != null && hydratedJurisdictions === query.data?.jurisdictions,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
};
